import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import PortalLayout from '../components/PortalLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Calendar, Send, Plus, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InterviewInviteCreate = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const candidateId = searchParams.get('candidateId');
  
  const [candidate, setCandidate] = useState(null);
  const [sending, setSending] = useState(false);
  
  const [formData, setFormData] = useState({
    interview_type: 'video_call',
    proposed_times: ['', '', ''],
    location_or_link: '',
    message: ''
  });

  useEffect(() => {
    if (candidateId) {
      fetchCandidate();
    }
  }, [candidateId]);

  const fetchCandidate = async () => {
    try {
      const response = await axios.get(`${API}/portal/talent/candidate/${candidateId}`);
      setCandidate(response.data.candidate);
    } catch (error) {
      toast.error('Failed to load candidate');
      navigate('/portal/talent');
    }
  };

  const updateProposedTime = (index, value) => {
    const newTimes = [...formData.proposed_times];
    newTimes[index] = value;
    setFormData({ ...formData, proposed_times: newTimes });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!candidateId) {
      toast.error('Candidate ID required');
      return;
    }
    
    // Filter out empty proposed times
    const validTimes = formData.proposed_times.filter(t => t && t.trim());
    
    if (validTimes.length === 0) {
      toast.error('Please provide at least one proposed time');
      return;
    }
    
    setSending(true);
    
    try {
      const inviteData = {
        candidate_user_id: candidateId,
        interview_type: formData.interview_type,
        proposed_times: validTimes,
        location_or_link: formData.location_or_link,
        message: formData.message
      };
      
      await axios.post(`${API}/portal/talent/interviews`, inviteData);
      
      toast.success('Interview invitation sent!');
      navigate(`/portal/talent/${candidateId}`);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setSending(false);
    }
  };

  return (
    <PortalLayout>
      <div className="max-w-3xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">Invite to Interview</h1>
          {candidate && (
            <p className="text-muted-foreground">
              Inviting {candidate.display_name} to interview
            </p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Interview Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium text-foreground block mb-2">
                  Interview Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.interview_type}
                  onChange={(e) => setFormData({ ...formData, interview_type: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background"
                  required
                >
                  <option value="phone">Phone Call</option>
                  <option value="video_call">Video Call</option>
                  <option value="in_person">In-Person</option>
                </select>
              </div>

              <div>
                <label className="text-sm font-medium text-foreground block mb-2">
                  Proposed Times (up to 3 options)
                </label>
                <div className="space-y-2">
                  {[0, 1, 2].map((index) => (
                    <Input
                      key={index}
                      type="datetime-local"
                      value={formData.proposed_times[index]}
                      onChange={(e) => updateProposedTime(index, e.target.value)}
                      placeholder={`Option ${index + 1}`}
                    />
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Provide multiple time options for the candidate to choose from
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-foreground block mb-2">
                  {formData.interview_type === 'in_person' ? 'Location' : 'Meeting Link'}
                </label>
                <Input
                  value={formData.location_or_link}
                  onChange={(e) => setFormData({ ...formData, location_or_link: e.target.value })}
                  placeholder={
                    formData.interview_type === 'in_person' 
                      ? 'e.g., 123 Main St, Sydney NSW'
                      : 'e.g., https://meet.google.com/abc-defg-hij'
                  }
                />
              </div>

              <div>
                <label className="text-sm font-medium text-foreground block mb-2">
                  Message to Candidate <span className="text-red-500">*</span>
                </label>
                <Textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Provide details about the interview format, what to prepare, and any other relevant information..."
                  rows={5}
                  required
                />
              </div>

              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <p className="text-sm text-foreground">
                  💡 The candidate can accept one of your proposed times, decline, or suggest an alternative.
                </p>
              </div>
            </CardContent>
          </Card>

          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => navigate(`/portal/talent/${candidateId}`)}
              className="flex-1 px-6 py-3 border border-border rounded-lg hover:bg-muted transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={sending}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
            >
              <Send className="w-5 h-5" />
              {sending ? 'Sending...' : 'Send Invitation'}
            </button>
          </div>
        </form>
      </div>
    </PortalLayout>
  );
};

export default InterviewInviteCreate;
